import { type ChangeEvent } from 'react';
import { Add, Cross } from '@/components/icons';
import { Input } from '@/components/ui/input';
import ColorSwatchPicker from './ColorSwatchPicker';
import { BAND_SWATCH_DEFAULT, CUTOFF_SWATCH_DEFAULT, type BandRow, type CutoffRow } from './types';
import type { ChartTypeDecorationsCopy } from '../chart-types/registry';

interface DecorationsEditorProps {
    /** Type-aware wording; omitted fields fall back to the generic cartesian labels. */
    copy?: ChartTypeDecorationsCopy;
    bands: BandRow[];
    cutoffs: CutoffRow[];
    yAxisMin: string;
    yAxisMax: string;
    disabled?: boolean;
    onAddBand: () => void;
    onUpdateBand: (index: number, patch: Partial<BandRow>) => void;
    onRemoveBand: (index: number) => void;
    onAddCutoff: () => void;
    onUpdateCutoff: (index: number, patch: Partial<CutoffRow>) => void;
    onRemoveCutoff: (index: number) => void;
    onYAxisMinChange: (value: string) => void;
    onYAxisMaxChange: (value: string) => void;
}

const AddButton = ({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-50"
    >
        <Add className="size-3" /> Adicionar
    </button>
);

/**
 * The shared reference-bands / cutoff-lines / y-scale editor. Cartesian charts
 * read it literally; gauge/bullet reuse it to mean colored zones + value range,
 * so the labels come from the type's `decorationsCopy`.
 */
export default function DecorationsEditor({
    copy,
    bands,
    cutoffs,
    yAxisMin,
    yAxisMax,
    disabled = false,
    onAddBand,
    onUpdateBand,
    onRemoveBand,
    onAddCutoff,
    onUpdateCutoff,
    onRemoveCutoff,
    onYAxisMinChange,
    onYAxisMaxChange,
}: DecorationsEditorProps) {
    const bandsLabel = copy?.bandsLabel ?? 'Faixas de referência';
    const cutoffLabel = copy?.cutoffLabel ?? 'Linhas de corte';
    const scaleLabel = copy?.scaleLabel ?? 'Escala do eixo Y';

    return (
        <div className="space-y-4">
            {copy?.hint && <p className="text-xs text-muted-foreground">{copy.hint}</p>}

            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">{bandsLabel}</span>
                    <AddButton onClick={onAddBand} disabled={disabled} />
                </div>
                {bands.map((band, index) => (
                    <div key={`band-${index}`} className="flex items-center gap-1.5">
                        <Input
                            value={band.label}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                onUpdateBand(index, { label: event.target.value })
                            }
                            placeholder="Rótulo"
                            className="h-8 flex-1 text-xs"
                        />
                        <Input
                            value={band.min}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                onUpdateBand(index, { min: event.target.value })
                            }
                            placeholder="mín"
                            inputMode="decimal"
                            className="h-8 w-14 text-xs"
                        />
                        <Input
                            value={band.max}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                onUpdateBand(index, { max: event.target.value })
                            }
                            placeholder="máx"
                            inputMode="decimal"
                            className="h-8 w-14 text-xs"
                        />
                        <ColorSwatchPicker
                            value={band.color}
                            defaultColor={BAND_SWATCH_DEFAULT}
                            onChange={(color) => onUpdateBand(index, { color })}
                            ariaLabel="Cor da faixa"
                        />
                        <button
                            type="button"
                            onClick={() => onRemoveBand(index)}
                            className="flex size-8 flex-shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                            aria-label="Remover faixa"
                        >
                            <Cross className="size-3.5" />
                        </button>
                    </div>
                ))}
            </div>

            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">{cutoffLabel}</span>
                    <AddButton onClick={onAddCutoff} disabled={disabled} />
                </div>
                {cutoffs.map((cutoff, index) => (
                    <div key={`cutoff-${index}`} className="flex items-center gap-1.5">
                        <Input
                            value={cutoff.label}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                onUpdateCutoff(index, { label: event.target.value })
                            }
                            placeholder="Rótulo"
                            className="h-8 flex-1 text-xs"
                        />
                        <Input
                            value={cutoff.value}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                onUpdateCutoff(index, { value: event.target.value })
                            }
                            placeholder="valor"
                            inputMode="decimal"
                            className="h-8 w-16 text-xs"
                        />
                        <ColorSwatchPicker
                            value={cutoff.color}
                            defaultColor={CUTOFF_SWATCH_DEFAULT}
                            onChange={(color) => onUpdateCutoff(index, { color })}
                            ariaLabel="Cor da linha"
                        />
                        <button
                            type="button"
                            onClick={() => onRemoveCutoff(index)}
                            className="flex size-8 flex-shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                            aria-label="Remover linha"
                        >
                            <Cross className="size-3.5" />
                        </button>
                    </div>
                ))}
            </div>

            <div className="space-y-2">
                <span className="text-xs font-medium text-muted-foreground">{scaleLabel}</span>
                <div className="flex items-center gap-1.5">
                    <Input
                        value={yAxisMin}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            onYAxisMinChange(event.target.value)
                        }
                        placeholder="mín"
                        inputMode="decimal"
                        className="h-8 flex-1 text-xs"
                        disabled={disabled}
                    />
                    <Input
                        value={yAxisMax}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            onYAxisMaxChange(event.target.value)
                        }
                        placeholder="máx"
                        inputMode="decimal"
                        className="h-8 flex-1 text-xs"
                        disabled={disabled}
                    />
                </div>
            </div>
        </div>
    );
}
